import { useEffect, useState, useCallback } from "react";

/**
 * useTheme
 * - Reads stored preference ("light" | "dark") or falls back to system.
 * - `effective` is the currently-applied theme.
 * - `setTheme("system")` clears manual preference and returns to system.
 */
const STORAGE_KEY = "aura-theme";

export default function useTheme() {
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "system";
    return localStorage.getItem(STORAGE_KEY) || "system";
  });
  const [systemDark, setSystemDark] = useState(() => {
    if (typeof window === "undefined") return true;
    return !window.matchMedia("(prefers-color-scheme: light)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e) => setSystemDark(!e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const effective = mode === "system" ? (systemDark ? "dark" : "light") : mode;

  useEffect(() => {
    const root = document.documentElement;
    if (mode === "system") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", mode);
    }
    // Emit event for canvas/other listeners
    window.dispatchEvent(new CustomEvent("aura:theme", { detail: { effective } }));
  }, [mode, effective]);

  const setTheme = useCallback((value) => {
    if (value === "system") {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, value);
    }
    setMode(value);
  }, []);

  const toggle = useCallback(() => {
    setTheme(effective === "dark" ? "light" : "dark");
  }, [effective, setTheme]);

  return { mode, effective, setTheme, toggle };
}
